import type { FastIdeasSelector, FastIdeasSelectorEventBus, FastMovingIdea } from '.';
import { signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import type { IdeaDTO } from 'src/dtos/Idea';
import { injectable } from 'src/features/ioc';
import type { StoreAdapter } from 'src/models/StoreAdapter';
import type { SelectIdeasEvent } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';

@injectable()
export class FastIdeasSelectorImpl implements FastIdeasSelector {
    #disposableStack = new DisposableStack();
    #selectedFastIdeas: Signal<FastMovingIdea[]> = signal([]);
    #persistSelection: ((ideaIds: string[]) => void) | null = null;
    #onIdeasSelected?: (payload: SelectIdeasEvent['widget::onIdeasSelect']) => void;

    eventBus: FastIdeasSelectorEventBus = EventBusBuilder.create()
        .addSignal('widget::selectedFastIdeas', () => this.#selectedFastIdeas)
        .addEvent('widget::onIdeasSelect', (payload) => {
            const ideas = payload.ideas.filter((idea): idea is IdeaDTO => 'idea' in idea);
            const ideaIds = ideas.map((idea) => idea.idea.id as string);
            this.#handleSelect(ideaIds);

            this.#onIdeasSelected?.(payload);
        })
        .build();

    #handleSelect(ideaIds: string[]): void {
        this.#selectedFastIdeas.value = ideaIds.map((id) => ({
            type: 'idea' as const,
            id,
        }));

        this.#persistSelection?.(ideaIds);
    }

    #syncFromIdeaIds(ideaIds: string[]): void {
        const currentIds = this.#selectedFastIdeas.value.map((idea) => idea.id);
        const idsChanged = ideaIds.length !== currentIds.length || ideaIds.some((id, i) => id !== currentIds[i]);

        if (idsChanged) {
            this.#selectedFastIdeas.value = ideaIds.map((id) => ({
                type: 'idea' as const,
                id,
            }));
        }
    }

    init<T>(
        adapter: StoreAdapter<T>,
        lens: {
            get: (state: T) => string[];
            set: (ideaIds: string[]) => Partial<T>;
            onIdeasSelected?: (payload: SelectIdeasEvent['widget::onIdeasSelect']) => void;
        },
    ): void {
        this.#persistSelection = (ideaIds) => adapter.persist(lens.set(ideaIds));
        this.#onIdeasSelected = lens.onIdeasSelected;

        // Sync initial state
        this.#syncFromIdeaIds(lens.get(adapter.getState()));

        // Subscribe to state changes
        const unsubscribe = adapter.subscribe((state) => {
            this.#syncFromIdeaIds(lens.get(state));
        });
        this.#disposableStack.defer(unsubscribe);
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
